import { useMemo } from 'react';
import type { Keyframes } from 'styled-components';
import type { TabContentAnimations } from './Tabs.types';
import {
  fadeIn,
  fadeOut,
  slideInFromLeft,
  slideInFromRight,
  slideOutToLeft,
  slideOutToRight,
  fadeSlideInFromLeft,
  fadeSlideInFromRight,
  fadeSlideOutToLeft,
  fadeSlideOutToRight,
} from './tabContentAnimations';
import { tabAnimationCss } from './tabAnimationCss';

export function useTabContentAnimations({
  activeTabIndex,
  prevActiveTabIndex,
  tabContentAnimation,
}: {
  activeTabIndex: number | null;
  prevActiveTabIndex: number | null;
  tabContentAnimation?: TabContentAnimations;
}) {
  const animations: {
    prev: [Keyframes | string, number, string, number],
    active: [Keyframes | string, number, string, number],
  } = useMemo(() => {
    if (
      !tabContentAnimation
      || prevActiveTabIndex === null
      || activeTabIndex === null
      || prevActiveTabIndex === activeTabIndex
    ) {
      return {
        prev: ['none', 0, 'linear', 0],
        active: ['none', 0, 'linear', 0],
      };
    }
    if (tabContentAnimation === 'fade') {
      return {
        prev: [fadeOut, 200, 'linear', 0],
        active: [fadeIn, 250, 'linear', 250],
      };
    }
    const isEnteringFromRight = (
      activeTabIndex === null
      || activeTabIndex > prevActiveTabIndex
    );
    if (tabContentAnimation === 'slide') {
      if (isEnteringFromRight) {
        return {
          prev: [slideOutToLeft, 300, 'ease', 0],
          active: [slideInFromRight, 300, 'ease', 0],
        };
      }
      return {
        prev: [slideOutToRight, 300, 'ease', 0],
        active: [slideInFromLeft, 300, 'ease', 0],
      };
    }
    if (tabContentAnimation === 'fade+slide') {
      if (isEnteringFromRight) {
        return {
          prev: [fadeSlideOutToLeft, 250, 'ease', 0],
          active: [fadeSlideInFromRight, 300, 'ease', 0],
        };
      }
      return {
        prev: [fadeSlideOutToRight, 250, 'ease', 0],
        active: [fadeSlideInFromLeft, 300, 'ease', 0],
      };
    }
    return {
      prev: ['none', 0, 'linear', 0],
      active: ['none', 0, 'linear', 0],
    };
  }, [
    activeTabIndex,
    prevActiveTabIndex,
    tabContentAnimation,
  ]);

  const prevTabCss = tabAnimationCss(animations.prev);
  const activeTabCss = tabAnimationCss(animations.active);

  return {
    prevTabCss,
    activeTabCss,
  };
}
